import { observer } from "mobx-react-lite";
import { useState } from "react";
import styled from "styled-components";

import { count } from "../../count";
import { EXAMPLE_SONG } from "../../exampleSong";
import { useStores } from "../../stores/useStores";
import { Button, IconButton, Pill } from "../ui/controls";
import { Icon } from "../ui/Icon";
import { Empty, List, PaneHeader, PaneTitle, Row, RowDetail, RowText, RowTitle } from "../ui/lists";
import { SearchBox } from "../ui/SearchBox";
import { AddSongDialog } from "./AddSongDialog";

/** All songs, with search. */
export const SongList = observer(function SongList({ selectedId }: { selectedId?: string }) {
    const { library, navigation } = useStores();
    const [query, setQuery] = useState("");
    const [adding, setAdding] = useState(false);

    const open = (songId: string) => navigation.go({ name: "songs", songId });

    async function addExample() {
        const { song } = await library.change((l) => l.addSong(EXAMPLE_SONG.details, EXAMPLE_SONG.text));

        open(song.id);
    }

    return (
        <>
            <PaneHeader>
                <PaneTitle>Songs</PaneTitle>
                <IconButton aria-label="Add a song" $active onClick={() => setAdding(true)}>
                    <Icon name="plus" />
                </IconButton>
            </PaneHeader>
            {library.songs.length === 0 ? (
                <Empty>
                    <span>There are no songs yet. Add one, or start with an example to try things out.</span>
                    <Button $kind="primary" onClick={() => setAdding(true)}>
                        Add a song
                    </Button>
                    <Button onClick={addExample}>Add an example song</Button>
                </Empty>
            ) : (
                <>
                    <Search label="Search songs" placeholder="Search title or artist" value={query} onChange={setQuery} />
                    <List>
                        {library.findSongs(query).map((song) => {
                            const versions = library.versionsOf(song.id).length;

                            return (
                                <li key={song.id}>
                                    <Row aria-current={song.id === selectedId} onClick={() => open(song.id)}>
                                        <RowText>
                                            <RowTitle>{song.title}</RowTitle>
                                            {song.artist && <RowDetail>{song.artist}</RowDetail>}
                                        </RowText>
                                        {versions > 1 && <Pill>{count(versions, "version")}</Pill>}
                                    </Row>
                                </li>
                            );
                        })}
                    </List>
                </>
            )}
            {adding && <AddSongDialog onClose={() => setAdding(false)} />}
        </>
    );
});

const Search = styled(SearchBox)`
    display: block;
    padding: 8px 16px 12px;
`;
